import React, {useState} from 'react';
import {Image, Modal, Pressable, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {Colors} from '../../../../constants/Colors';
import {ProductInCart, useBagStore} from '../../../../store/BagStore';
import Counter from '../../../Home/components/ProductList/Counter/Counter';
import {styles} from './styles';
import { useNavigation } from '@react-navigation/native';
import {Images} from '../../../../constants/Images';

export type UnavailableReason = 
  | 'not_found' 
  | 'insufficient_stock' 
  | 'price_mismatch' 
  | 'catalog_not_found';

export interface UnavailableInfo {
  description?: string;
  sku: string;
  availableQty?: number;
  reason: UnavailableReason;
  message: string;
  correctPrice?: number;
  sentPrice?: number;
  product?: {
    SKU: string;
    DESCRICAO: string;
    COMPLEMENTO: string;
    PRECO: number;
    [key: string]: any;
  };
}

interface IBagList {
  item: ProductInCart;
  index: number;
  forceRender: () => void;
  onPress?: () => void;
  complete?: boolean;
  markAsUnavailable?: boolean;
  isUnavailable?: UnavailableInfo;
  setOpenRecomendations?: (value: boolean) => void;
}

const BagList = ({
  item, 
  index, 
  forceRender, 
  onPress, 
  complete = true, 
  markAsUnavailable = false, 
  isUnavailable = undefined,
  setOpenRecomendations
}: IBagList) => {
  const {updateProductQuantity, products} = useBagStore();
  const removeProduct = useBagStore(state => state.removeProduct);
  const navigation = useNavigation();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const itemKey = (item as any)?.cartKey ?? item.sku;

  const handleRemoveProduct = () => {
    setShowRemoveConfirm(false);
    forceRender();
    removeProduct(itemKey);
  };

  const isPromo = itemKey.endsWith('_promo');
  const otherKey = isPromo ? item.sku : `${item.sku}_promo`;
  const otherQty = products.find(p => ((p as any).cartKey ?? p.sku) === otherKey)?.quantity ?? 0;
  const stockLimit = products[index]?.maximum ? products[index]?.maximum : products[index]?.estoque;
  const effectiveMax = Math.max(0, stockLimit - otherQty);

  const incrementQuantity = val => {
    updateProductQuantity(itemKey, val);
  };

  const decrementQuantity = count => {
    if (item?.quantity > 1) {
      updateProductQuantity(itemKey, item?.quantity - 1);
    } else {
      updateProductQuantity(itemKey, 0);
      forceRender();
    }
  };

  const handlePress = () => {
    navigation.navigate('ProductDetails', {item: item, fromBag: true});
  }

  // Retorna as informações de exibição baseado no tipo de indisponibilidade
  const getUnavailableDisplay = (): { 
    borderColor: string; 
    badgeColor: string; 
    badgeText: string;
    icon: string;
  } | null => {
    if (!markAsUnavailable || !isUnavailable) return null;

    switch (isUnavailable.reason) {
      case 'price_mismatch':
        return {
          borderColor: '#f39c12',
          badgeColor: '#f39c12',
          badgeText: `Preço atualizado: ${formatCurrency(isUnavailable.correctPrice || 0)}`,
          icon: '💰',
        };
      case 'insufficient_stock':
        if ((isUnavailable.availableQty ?? 0) > 0) {
          return {
            borderColor: '#e67e22',
            badgeColor: '#e67e22',
            badgeText: `Disponível: ${isUnavailable.availableQty} un.`,
            icon: '📦',
          };
        }
        return {
          borderColor: '#e74c3c',
          badgeColor: '#e74c3c',
          badgeText: 'Estoque esgotado',
          icon: '📦',
        };
      case 'not_found':
        return {
          borderColor: '#e74c3c',
          badgeColor: '#e74c3c',
          badgeText: 'Sem estoque',
          icon: '❌',
        };
      case 'catalog_not_found':
        return {
          borderColor: '#95a5a6',
          badgeColor: '#95a5a6',
          badgeText: 'Fora do catálogo',
          icon: '🚫',
        };
      default:
        return {
          borderColor: '#e74c3c',
          badgeColor: '#e74c3c',
          badgeText: 'Indisponível',
          icon: '⚠️',
        };
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const unavailableDisplay = getUnavailableDisplay();

  // Verifica se deve mostrar comparação de preço (quando o preço mudou)
  const showPriceComparison = isUnavailable?.reason === 'price_mismatch' && 
    isUnavailable.correctPrice !== undefined && 
    isUnavailable.sentPrice !== undefined;

  return (
    <TouchableOpacity
      style={{flexDirection: 'row', alignItems: 'center'}}
      onPress={handlePress}
    >
      <View
        style={[
          styles.card,
          markAsUnavailable && unavailableDisplay ? {
            borderColor: unavailableDisplay.borderColor,
            borderWidth: 1.5,
          } : {},
          {position: 'relative'}
        ]}
      >
        {/* Imagem do produto */}
        <View style={{position: 'relative'}}>
          <Image
            source={item?.image ? {uri: item.image} : Images.emptyImgIcon}
            style={[
              {
                width: 70,
                height: 70,
                marginRight: 5,
              },
              markAsUnavailable ? {opacity: 0.5} : {}
            ]}
          />
          {/* Overlay de indisponível na imagem */}
          {markAsUnavailable && unavailableDisplay && (
            <View style={design.imageOverlay}>
              <Text style={design.imageOverlayIcon}>{unavailableDisplay.icon}</Text>
            </View>
          )}
        </View>

        {/* Informações do produto */}
        <View style={[styles.spaceItens, markAsUnavailable ? {opacity: 0.7} : {}]}>
          <Text style={styles.description} numberOfLines={4}>
            {products[index]?.descricao + ' ' + (products[index]?.complemento || '').replace('- R$', '').replace(/\\n/g, '')}
          </Text>
          {complete ? (
            <Counter
              width="50%"
              hasButton={false}
              counterPosition="flex-start"
              mt={0}
              quantity={products[index]?.quantity}
              increment={(val: number) => incrementQuantity(val)}
              decrement={(count: number) => decrementQuantity(count)}
              max={effectiveMax}
              item={products[index]}
              bag
            />
          ) : (
            <Text style={styles.quantity}>
              Quantidade: {products[index]?.quantity}
            </Text>
          )}
        </View>

        {/* Área de preço */}
        <View style={styles.spaceA}>
          {/* Exibição de preços */}
          {showPriceComparison ? (
            // Quando há divergência de preço, mostra o preço antigo riscado e o novo
            <>
              <Text style={design.oldPriceStrikethrough}>
                {formatCurrency(isUnavailable!.sentPrice!)}
              </Text>
              <Text style={design.newPriceHighlight}>
                {formatCurrency(isUnavailable!.correctPrice!)}
              </Text>
            </>
          ) : (
            // Exibição normal de preços
            <>
              {products[index]?.discountedPrice < products[index]?.preco && (
                <Text style={styles.originalPrice}>
                  {Number(products[index]?.preco).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </Text>
              )}
              <Text
                style={[
                  styles.discountedPrice,
                  {
                    color:
                      products[index]?.discountedPrice < products[index]?.preco
                        ? 'green'
                        : 'gray',
                  },
                ]}>
                {Number(products[index]?.discountedPrice).toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                })}
              </Text>
            </>
          )}
        </View>

        {/* Badge de indisponibilidade */}
        {markAsUnavailable && unavailableDisplay && (
          <View 
            style={[
              design.unavailableBadge, 
              {backgroundColor: unavailableDisplay.badgeColor}
            ]}
          >
            <Text style={design.unavailableBadgeText}>
              {unavailableDisplay.badgeText}
            </Text>
          </View>
        )}
      </View>

      {/* Botão de remover, em coluna própria */}
      {complete && (
        <TouchableOpacity
          style={design.deleteColumn}
          hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}
          onPress={() => setShowRemoveConfirm(true)}>
          <View style={design.deleteButton}>
            <Ionicons name="trash-outline" color="#fff" size={20} />
          </View>
        </TouchableOpacity>
      )}

      <Modal
        visible={showRemoveConfirm}
        transparent
        animationType="fade"
        onRequestClose={() => setShowRemoveConfirm(false)}>
        <View style={design.modalOverlay}>
          <View style={design.modalContainer}>
            <View style={design.modalContent}>
              <Text style={design.modalTitle}>Remover produto</Text>
              <Text style={design.modalMessage}>
                Deseja remover {products[index]?.descricao} da sacola?
              </Text>
            </View>
            <View style={design.modalButtons}>
              <Pressable style={design.modalButtonConfirm} onPress={handleRemoveProduct}>
                <Text style={design.modalButtonConfirmText}>Remover</Text>
              </Pressable>
              <Pressable
                style={design.modalButtonCancel}
                onPress={() => setShowRemoveConfirm(false)}>
                <Text style={design.modalButtonCancelText}>Cancelar</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </TouchableOpacity>
  );
};

const design = StyleSheet.create({
  // Coluna dedicada do botão de remover
  deleteColumn: {
    width: 56,
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.RedPallete.red,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Modal de confirmação de remoção
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 30,
  },
  modalContainer: {
    backgroundColor: 'white',
    borderRadius: 8,
    width: '100%',
    maxWidth: 340,
    overflow: 'hidden',
  },
  modalContent: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 20,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 16,
    fontFamily: 'TCCC-UnityText Bold',
    color: 'black',
    textAlign: 'center',
    marginBottom: 12,
  },
  modalMessage: {
    fontSize: 13,
    fontFamily: 'TCCC-UnityHeadline Regular',
    color: Colors.GrayPallete.grayOne,
    textAlign: 'center',
    lineHeight: 20,
  },
  modalButtons: {
    flexDirection: 'column',
    paddingHorizontal: 24,
    paddingBottom: 20,
    gap: 10,
  },
  modalButtonConfirm: {
    height: 40,
    backgroundColor: Colors.RedPallete.red,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonConfirmText: {
    fontSize: 14,
    fontFamily: 'TCCC-UnityText Bold',
    color: 'white',
    lineHeight: 16,
  },
  modalButtonCancel: {
    height: 40,
    borderWidth: 1,
    borderColor: Colors.RedPallete.red,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonCancelText: {
    fontSize: 14,
    fontFamily: 'TCCC-UnityText Bold',
    color: Colors.RedPallete.red,
    lineHeight: 16,
  },

  // Overlay na imagem quando indisponível
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.6)',
    marginRight: 5,
  },
  imageOverlayIcon: {
    fontSize: 24,
  },

  // Badge de indisponibilidade
  unavailableBadge: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },
  unavailableBadgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
  },

  // Preços para divergência
  oldPriceStrikethrough: {
    fontSize: 12,
    color: '#999',
    textDecorationLine: 'line-through',
    textAlign: 'right',
  },
  newPriceHighlight: {
    fontSize: 14,
    color: '#f39c12',
    fontWeight: '700',
    textAlign: 'right',
  },
});

export default BagList;